import { useRef } from 'react';
import { getGameById } from '@/config/games';
import type { ProjectSettingsSection } from '@/config/settingsSections';
import { useGameStore } from '@/stores/game.store';
import { ConfigOverviewPage } from '@/pages/ConfigOverviewPage';
import { FilesPage } from './FilesPage';
import { ServersPage } from './ServersPage';
import styles from './ConfigSettingsPage.module.scss';

// ============================================================
// Paramètres du projet actif : Pack / Fichiers / Containers
// ============================================================
// Ces trois écrans étaient l'entrée Configuration > Paramètres (trois onglets d'une page). Ils
// vivent désormais dans les Paramètres généraux, une section chacun (/parametres/packs,
// /parametres/fichiers, /parametres/containers), la navigation étant celle de la barre latérale.
// L'accès est gardé par la route (config/settingsSections) : chacun garde le droit qui l'ouvrait.
//
// Les trois sections rendent CE composant à la même position de l'arbre (GlobalSettingsPage) : il
// n'est pas remonté en passant de l'une à l'autre, et les sections déjà visitées restent MONTÉES,
// masquées en CSS. L'éditeur de fichiers garde son texte dans un état local, un démontage perdrait
// une édition en cours sans le moindre avertissement.
// ============================================================

const HEAD_OF: Record<ProjectSettingsSection, { title: string; subtitle: string }> = {
  packs: {
    title: 'Pack',
    subtitle: 'Groupes de packs (behavior & resource) du projet, et leur synchronisation.',
  },
  fichiers: {
    title: 'Fichiers',
    subtitle: 'Explorer et éditer les fichiers du pack, behavior comme resource.',
  },
  containers: {
    title: 'Containers',
    subtitle: 'Éditer les configs des containers de prod (data/mods), puis envoyer une version.',
  },
};

export function ConfigSettingsPage({ section }: { section: ProjectSettingsSection }) {
  const gameName = useGameStore((s) => getGameById(s.activeGameId).name);
  const head = HEAD_OF[section];

  // Sections déjà ouvertes : montées une fois, jamais démontées, pour ne pas perdre une édition.
  const visited = useRef<Set<ProjectSettingsSection>>(new Set([section]));
  visited.current.add(section);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{head.title}</h1>
        <p className={styles.subtitle}>
          {head.subtitle} Projet actif : <strong>{gameName}</strong>.
        </p>
      </header>

      {/* La chaîne de hauteur est reproduite ici : Fichiers et Containers reposent sur un .page en
          height: 100%, qui a besoin d'un parent à hauteur bornée pour que leurs deux colonnes
          gardent leur propre défilement au lieu d'étirer la page entière. */}
      <div className={styles.tabBody}>
        {visited.current.has('packs') && (
          <div className={section === 'packs' ? styles.paneOn : styles.paneOff}>
            <ConfigOverviewPage />
          </div>
        )}
        {visited.current.has('fichiers') && (
          <div className={section === 'fichiers' ? styles.paneOn : styles.paneOff}>
            <FilesPage />
          </div>
        )}
        {visited.current.has('containers') && (
          <div className={section === 'containers' ? styles.paneOn : styles.paneOff}>
            <ServersPage />
          </div>
        )}
      </div>
    </div>
  );
}
